import React, { useState, useEffect } from 'react';
import Card from './ui/Card';
import Badge from './ui/Badge';
import Button from './ui/Button';

const PayRateCalculator = () => {
  const [qualification, setQualification] = useState(0);
  const [experience, setExperience] = useState(0);
  const [englishLevel, setEnglishLevel] = useState(0);
  const [competencies, setCompetencies] = useState([]);

  const [totalScore, setTotalScore] = useState(0);
  const [payRate, setPayRate] = useState('B');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const COMPETENCE_OPTIONS = [
    'IN1-2', 'IN3-CH2', 'CH3-4', 'CH5-6', 'Y3-5',
    'Y6-8/T5', 'T6-T7', 'AD-B1', 'AD-B2', 'AD-C1',
    'AD-C2', 'EP B1/B2', 'EP C1/C2', 'Business/Uni'
  ];

  const PAY_RATES = {
    'B': { minScore: 0, maxScore: 8, color: '#1E40AF', label: 'Rate B' },
    'C': { minScore: 9, maxScore: 13, color: '#7C3AED', label: 'Rate C' },
    'D': { minScore: 14, maxScore: 16, color: '#059669', label: 'Rate D' },
  };

  const QUALIFICATIONS = [
    { value: 1, label: 'EQF Level 4 or below', description: 'None or recognised TEFL certificate.' },
    { value: 2, label: 'EQF Level 5', description: 'CELTA / CertTESOL or Bachelors degree related to teaching.' },
    { value: 3, label: 'EQF Level 6', description: 'CertPT or equivalent with internal assessed teaching module.' },
    { value: 4, label: 'EQF Level 7', description: 'Masters degree, PGCE, DELTA or DipTESOL.' },
  ];

  const EXPERIENCES = [
    { value: 1, label: '0-3 years', description: 'Early career teacher.' },
    { value: 2, label: '4-7 years', description: 'Developing experience.' },
    { value: 3, label: '8-11 years', description: 'Established teacher.' },
    { value: 4, label: '12+ years', description: 'Highly experienced teacher.' },
  ];

  const ENGLISH_LEVELS = [
    { value: 1, label: 'Pre C1' },
    { value: 2, label: 'Unaccredited C1/C2' },
    { value: 3, label: 'Cambridge C1 / Native' },
    { value: 4, label: 'Cambridge C2' },
  ];

  const handleCompetenceChange = (option) => {
    if (competencies.includes(option)) {
      setCompetencies(competencies.filter(c => c !== option));
    } else {
      setCompetencies([...competencies, option]);
    }
  };

  useEffect(() => {
    let compScore = 0;
    const count = competencies.length;
    if (count >= 11) compScore = 4;
    else if (count >= 8) compScore = 3;
    else if (count >= 5) compScore = 2;
    else if (count >= 1) compScore = 1;

    const score = parseInt(qualification) + parseInt(experience) + parseInt(englishLevel) + compScore;
    setTotalScore(score);

    if (score >= 14) {
      setPayRate('D');
    } else if (score >= 9) {
      setPayRate('C');
    } else {
      setPayRate('B');
    }
  }, [qualification, experience, englishLevel, competencies]);

  const handleReset = () => {
    setQualification(0);
    setExperience(0);
    setEnglishLevel(0);
    setCompetencies([]);
    setShowSuggestions(false);
  };

  const getCompetencyScore = () => {
    const count = competencies.length;
    if (count >= 11) return 4;
    else if (count >= 8) return 3;
    else if (count >= 5) return 2;
    else if (count >= 1) return 1;
    return 0;
  };

  const renderSuggestions = () => {
    if (payRate === 'D') return <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>You've reached the highest rate!</p>;

    const target = payRate === 'B' ? 9 : 14;
    const needed = target - totalScore;
    const nextRate = payRate === 'B' ? 'C' : 'D';
    const compScore = getCompetencyScore();

    return (
      <>
        <p style={{ fontWeight: 600, marginBottom: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
          Need {needed} more pt{needed !== 1 ? 's' : ''} for Rate {nextRate}
        </p>
        <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', fontSize: 'var(--font-size-xs)', margin: '0' }}>
          {qualification < 4 && <li>Qualification: +{4 - qualification}</li>}
          {experience < 4 && <li>Experience: +{4 - experience}</li>}
          {englishLevel < 4 && <li>English: +{4 - englishLevel}</li>}
          {compScore < 4 && <li>Competencies: +{4 - compScore}</li>}
        </ul>
      </>
    );
  };

  const gradeVariant = {
    'B': 'secondary',
    'C': 'purple',
    'D': 'success'
  }[payRate];

  return (
    <div style={{ padding: 'var(--spacing-sm)', height: '100vh', overflow: 'auto' }}>
      {/* 5 Column Layout */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--spacing-sm)', height: 'fit-content' }}>

        {/* Col 1: Qualification */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontWeight: 600, marginBottom: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
            Qualification
          </label>

          {/* Radio buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-md)' }}>
            {QUALIFICATIONS.map((q) => (
              <label key={q.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>
                <input
                  type="radio"
                  name="qualification"
                  value={q.value}
                  checked={qualification === q.value}
                  onChange={(e) => setQualification(Number(e.target.value))}
                  style={{ cursor: 'pointer' }}
                />
                <span>{q.label}</span>
              </label>
            ))}
          </div>

          {/* Score */}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)' }}>
            Score: <strong>{qualification}/4</strong>
          </div>

          {/* Static description */}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: '1.4' }}>
            <div style={{ marginBottom: 'var(--spacing-xs)' }}>Specific teacher training qualification.</div>
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              <li>EQF Level 4 or below: None or recognised TEFL certificate.</li>
              <li>EQF Level 5: CELTA / CertTESOL or Bachelors degree related to teaching.</li>
              <li>EQF Level 6: CertPT or equivalent with internal assessed teaching module.</li>
              <li>EQF Level 7: Masters degree, PGCE, DELTA or DipTESOL.</li>
            </ul>
          </div>
        </Card>

        {/* Col 2: Experience */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontWeight: 600, marginBottom: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
            Experience
          </label>

          {/* Radio buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-md)' }}>
            {EXPERIENCES.map((e) => (
              <label key={e.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>
                <input
                  type="radio"
                  name="experience"
                  value={e.value}
                  checked={experience === e.value}
                  onChange={(ev) => setExperience(Number(ev.target.value))}
                  style={{ cursor: 'pointer' }}
                />
                <span>{e.label}</span>
              </label>
            ))}
          </div>

          {/* Score */}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)' }}>
            Score: <strong>{experience}/4</strong>
          </div>

          {/* Static description */}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: '1.4' }}>
            Years of professional teaching experience in the same or a similar context - ESL instruction in language school / school environment using the communicative methodology.
          </div>
        </Card>

        {/* Col 3: English Level */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontWeight: 600, marginBottom: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
            English Level
          </label>

          {/* Radio buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', marginBottom: 'var(--spacing-md)' }}>
            {ENGLISH_LEVELS.map((eng) => (
              <label key={eng.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>
                <input
                  type="radio"
                  name="englishLevel"
                  value={eng.value}
                  checked={englishLevel === eng.value}
                  onChange={(e) => setEnglishLevel(Number(e.target.value))}
                  style={{ cursor: 'pointer' }}
                />
                <span>{eng.label}</span>
              </label>
            ))}
          </div>

          {/* Score */}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)' }}>
            Score: <strong>{englishLevel}/4</strong>
          </div>

          {/* Static description */}
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: '1.4' }}>
            <div style={{ marginBottom: 'var(--spacing-xs)' }}>Certified English language proficiency level.</div>
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              <li>Pre C1: B2 proficiency</li>
              <li>Unaccredited C1/C2: not accredited by Cambridge</li>
              <li>Cambridge C1: Grade C or above or Native speaker</li>
              <li>Cambridge C2: Grade C or above</li>
            </ul>
          </div>
        </Card>

        {/* Col 4: Competencies */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontWeight: 600, marginBottom: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
            Competencies
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--spacing-xs)',
            marginBottom: 'var(--spacing-sm)',
            flex: 1,
            overflowY: 'auto'
          }}>
            {COMPETENCE_OPTIONS.map((opt) => (
              <label
                key={opt}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 'var(--spacing-xs)',
                  border: competencies.includes(opt) ? '2px solid var(--color-secondary)' : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  cursor: 'pointer',
                  backgroundColor: competencies.includes(opt) ? 'var(--color-bg-hover)' : 'transparent',
                  transition: 'all var(--transition-base)',
                  fontSize: 'var(--font-size-xs)',
                  gap: 'var(--spacing-xs)'
                }}
              >
                <input
                  type="checkbox"
                  checked={competencies.includes(opt)}
                  onChange={() => handleCompetenceChange(opt)}
                  style={{ cursor: 'pointer', flex: 0 }}
                />
                <span style={{ flex: 1 }}>{opt}</span>
              </label>
            ))}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', paddingTop: 'var(--spacing-sm)', borderTop: '1px solid var(--color-border)' }}>
            <strong>{competencies.length}/14</strong> • Score: <strong>{getCompetencyScore()}/4</strong>
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)', lineHeight: '1.4' }}>
            <div>AD=Adults, EP=Exam Prep Intensive</div>
            <div>Scoring: 1-4 = 1pt, 5-7 = 2pts, 8-10 = 3pts, 11-14 = 4pts</div>
          </div>
        </Card>

        {/* Col 5: Pay Rate Result + Score Breakdown + Suggestions */}
        <Card style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'linear-gradient(135deg, var(--color-bg-surface) 0%, var(--color-bg-subtle) 100%)' }}>
          {/* Pay Rate Display */}
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
              Your Pay Rate
            </div>
            <div style={{
              fontSize: '2.5rem',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              color: PAY_RATES[payRate].color,
              marginBottom: 'var(--spacing-xs)'
            }}>
              {payRate}
            </div>
            <Badge variant={gradeVariant}>Total: {totalScore}</Badge>
          </div>

          {/* Score Breakdown */}
          <div style={{ marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginBottom: 'var(--spacing-xs)', color: 'var(--color-text-secondary)' }}>
              Score Breakdown
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--spacing-xs)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)' }}>
                <div style={{ fontWeight: 600, color: 'var(--color-secondary)' }}>{qualification}</div>
                <div style={{ color: 'var(--color-text-tertiary)' }}>Qual</div>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)' }}>
                <div style={{ fontWeight: 600, color: 'var(--color-secondary)' }}>{experience}</div>
                <div style={{ color: 'var(--color-text-tertiary)' }}>Exp</div>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)' }}>
                <div style={{ fontWeight: 600, color: 'var(--color-secondary)' }}>{englishLevel}</div>
                <div style={{ color: 'var(--color-text-tertiary)' }}>Eng</div>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)' }}>
                <div style={{ fontWeight: 600, color: 'var(--color-secondary)' }}>{getCompetencyScore()}</div>
                <div style={{ color: 'var(--color-text-tertiary)' }}>Comp</div>
              </div>
            </div>
          </div>

          {/* Rate thresholds */}
          <div style={{ marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#1E40AF', fontWeight: 600 }}>Rate B</span>
                <span>0 – 8 pts</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#7C3AED', fontWeight: 600 }}>Rate C</span>
                <span>9 – 13 pts</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#059669', fontWeight: 600 }}>Rate D</span>
                <span>14+ pts</span>
              </div>
            </div>
          </div>

          {/* Improvement Accordion */}
          <div>
            <button
              onClick={() => setShowSuggestions(!showSuggestions)}
              style={{
                width: '100%',
                padding: 'var(--spacing-xs)',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 600,
                fontSize: 'var(--font-size-xs)',
                marginBottom: showSuggestions ? 'var(--spacing-xs)' : 0
              }}
            >
              <span>Improve</span>
              <span>{showSuggestions ? '−' : '+'}</span>
            </button>
            {showSuggestions && (
              <div style={{ fontSize: 'var(--font-size-xs)', paddingTop: 'var(--spacing-xs)', borderTop: '1px solid var(--color-border)' }}>
                {renderSuggestions()}
              </div>
            )}
          </div>

          {/* Reset Button */}
          <Button variant="secondary" onClick={handleReset} style={{ marginTop: 'var(--spacing-sm)', fontSize: 'var(--font-size-xs)', padding: 'var(--spacing-xs)' }}>
            Reset
          </Button>
        </Card>
      </div>

      {/* Rate Legend */}
      <div style={{ marginTop: 'var(--spacing-md)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
        Rate B: 0-8 pts | Rate C: 9-13 pts | Rate D: 14+ pts
      </div>
    </div>
  );
};

export default PayRateCalculator;
